// Lib
import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

// Utils
import { getArea } from '../../../common/maths/geometry/rect/rectDimensionUtils';
import { toCssPx } from '../../utils/cssUtil';
import { getArrowPosition } from './floatingArrowUtils';
import { getFloatingPanelMountPoint } from './floatingPanelUtils';

// Components
import Icon from '../icons/Icon';
import delayRenderDecorator from '../../utils/react/delayRenderDecorator';

// Constants
import { HIDDEN_POSITION, TooltipPositions } from './tooltipConstants';

// Styles
import './FloatingArrow.scss';

interface Props {
    selector: string;
    fallbackSelector?: string;
    position: TooltipPositions;
    distance?: number;
    animate: boolean;
    delay?: number;
}

const FloatingArrow = (props: Props): ReactElement | null => {
    const { selector, fallbackSelector, position, distance = 0, animate } = props;
    const arrowRef = useRef<HTMLDivElement>(null);

    const updateArrowPosition = useCallback(() => {
        if (!arrowRef.current) return;

        const targetElement =
            document.querySelector(selector) || (!!fallbackSelector && document.querySelector(fallbackSelector));
        const newTargetRect = targetElement && targetElement.getBoundingClientRect();

        // If the target takes up no space on the screen, then don't show it
        if (!newTargetRect || getArea(newTargetRect) === 0) {
            arrowRef.current.style.top = `${HIDDEN_POSITION.top}px`;
            arrowRef.current.style.left = `${HIDDEN_POSITION.left}px`;
            return;
        }

        const arrowRect: DOMRect = arrowRef.current.getBoundingClientRect();
        const arrowPosition = getArrowPosition(newTargetRect, arrowRect, { position, distance });

        arrowRef.current.style.top = toCssPx(arrowPosition.top);
        arrowRef.current.style.left = toCssPx(arrowPosition.left);
    }, []);

    useEffect(() => {
        updateArrowPosition();
    });

    const floatingArrow = (
        <div ref={arrowRef} className={classNames('FloatingArrow', position, { animate })}>
            <Icon name="icon-arrow" />
        </div>
    );

    const portalMountPoint = getFloatingPanelMountPoint();

    if (!portalMountPoint) return null;

    return createPortal(floatingArrow, portalMountPoint);
};

export default delayRenderDecorator(FloatingArrow);
